export type CriteriaFramework =
  | 'picos'
  | 'pecos'
  | 'spider'
  | 'pico'
  | 'pcc'
  | 'spice'
  | 'other';

// All possible category keys across every framework
export type CriteriaCategory =
  | 'population'
  | 'intervention_exposure'
  | 'comparator_context'
  | 'outcome'
  | 'study_characteristics'
  | 'context_setting'
  | 'design'
  | 'other';

// Map of category keys to their include/exclude arrays
// passes: { population: { include: [], exclude: [] } }
// fails: { randomKey: { include: [], exclude: [] } }
export type CriteriaDetails = { include: string[]; exclude: string[] };
export type Criteria = Partial<Record<CriteriaCategory, CriteriaDetails>>;

export const Frameworks: CriteriaFramework[] = [
  'picos',
  'pecos',
  'spider',
  'pico',
  'pcc',
  'spice',
  'other',
];

type CriteriaFrameworkDetails = Record<
  CriteriaFramework,
  Partial<Record<CriteriaCategory, string>>
>;
// Mirrored by FRAMEWORKS in app/concepts/reviews/criteria_framework.rb: keep
// the framework keys, each framework's category set and its labels identical.
const FRAMEWORK_CATEGORIES: CriteriaFrameworkDetails = {
  picos: {
    population: 'Population',
    intervention_exposure: 'Intervention',
    comparator_context: 'Comparison',
    outcome: 'Outcome',
    study_characteristics: 'Study Design',
    other: 'Other',
  },
  pecos: {
    population: 'Population',
    intervention_exposure: 'Exposure',
    comparator_context: 'Comparison',
    outcome: 'Outcome',
    study_characteristics: 'Study Design',
    other: 'Other',
  },
  spider: {
    population: 'Sample',
    intervention_exposure: 'Phenomenon of Interest',
    design: 'Design',
    outcome: 'Evaluation',
    study_characteristics: 'Research Type',
    other: 'Other',
  },
  pico: {
    population: 'Population',
    intervention_exposure: 'Phenomenon of Interest',
    context_setting: 'Context',
    other: 'Other',
  },
  pcc: {
    population: 'Population',
    intervention_exposure: 'Concept',
    context_setting: 'Context',
    other: 'Other',
  },
  spice: {
    context_setting: 'Setting',
    population: 'Perspective',
    intervention_exposure: 'Intervention',
    comparator_context: 'Comparison',
    outcome: 'Evaluation',
    other: 'Other',
  },
  other: {
    other: 'Other',
  },
} as const;

// Default to picos with the pre-framework labels, for backward compatibility.
// Deliberately not mirrored in criteria_framework.rb (whose no-framework
// default uses the picos labels): the frontend never reads backend labels.
const LEGACY_DEFAULT_PICOS_CATEGORIES = {
  population: 'Population',
  intervention_exposure: 'Intervention / Exposure',
  comparator_context: 'Comparator / Context',
  outcome: 'Outcome',
  study_characteristics: 'Study Characteristics',
  other: 'Other',
} as const;

// Returns the category key → label map for a framework, falling back to legacy PICOS labels
export const getFrameworkCategoriesWithFallbackToPicos = (
  framework?: CriteriaFramework
): Record<string, string> => {
  if (!framework) {
    return LEGACY_DEFAULT_PICOS_CATEGORIES;
  }

  return FRAMEWORK_CATEGORIES[framework];
};

export const getCategoryLabels = (framework?: CriteriaFramework) => {
  const categories = getFrameworkCategoriesWithFallbackToPicos(framework);
  return Object.values(categories);
};

export const getCategoryKeys = (framework?: CriteriaFramework) => {
  const categories = getFrameworkCategoriesWithFallbackToPicos(framework);
  return Object.keys(categories) as CriteriaCategory[];
};
